<!--
  Histórico de informes detallados (vista de presentación). La lógica de datos vive en
  services/history-reports.service.js (y las llamadas axios en api/index.js); aquí solo
  estado, bindings y notificaciones. La tabla usa el estilo genérico compartido
  `.epc-data-table`. Filtro, orden y paginación son CLIENT-SIDE (el backend devuelve todos
  los registros): el filtro de texto/estado se aplica en `filteredHistory` y el orden/
  paginación los hace el propio b-table (sin backend-sorting/backend-pagination).
-->
<template>
  <div class="epc-data-table">
    <!-- Cabecera genérica: título + conteo (filtrado) + filtros -->
    <div class="epc-data-table__summary">
      <div class="epc-data-table__summary-title">
        <b-icon pack="fas" icon="history" size="is-small" />
        <span>{{ $t('report_history') }}</span>
        <span class="epc-data-table__count">{{ filteredHistory.length }}</span>
      </div>
      <div class="hr-filters flex flex-wrap items-center gap-2">
        <b-input v-model="searchText" :placeholder="$t('search')" size="is-small"
                 icon-pack="fas" icon="search" class="hr-filter w-full sm:w-56" />
        <b-input v-model="weekFilter" :placeholder="$t('week')" size="is-small"
                 type="number" min="1" max="53" class="hr-filter w-24" />
        <b-select v-model="statusFilter" size="is-small" class="hr-filter w-44">
          <option value="">{{ $t('all') }}</option>
          <option v-for="s in statusChoices" :key="s" :value="s">{{ s === 'error' ? $t('error') : s }}</option>
        </b-select>
        <b-button v-if="hasFilters" type="is-text" size="is-small" icon-pack="fas" icon-left="times"
                  @click="clearFilters">{{ $t('clean_filters') }}</b-button>
      </div>
    </div>

    <section class="epc-data-table__body">
      <b-table
        :data="filteredHistory"
        :loading="isLoadingTable"

        paginated
        :per-page="perPage"

        class="w-full text-sm"
        striped
        icon-pack="fas"
      >
        <!-- Informe y periodo: título (description sin la cola ' - Semana…') + periodo debajo -->
        <b-table-column field="description" :label="$t('report_and_period')" sortable v-slot="props">
          <div class="font-medium">
            {{ reportTitle(props.row) }}<span v-if="reportWeek(props.row)" class="text-gray-500 font-normal"> · {{ $t('week') }} {{ reportWeek(props.row) }}</span>
          </div>
          <div v-if="reportPeriod(props.row)" class="text-xs text-gray-500">
            {{ reportPeriod(props.row).from }} – {{ reportPeriod(props.row).to }}
          </div>
        </b-table-column>

        <b-table-column field="week" :label="$t('week')" width="80" centered v-slot="props">
          <span v-if="reportWeek(props.row)" class="font-medium">S{{ reportWeek(props.row) }}</span>
          <span v-else class="text-gray-400">—</span>
        </b-table-column>

        <b-table-column field="status" :label="$t('status')" sortable v-slot="props">
          <div class="flex items-center">
            <!-- El indicador de progreso solo mientras se genera; al terminar no ocupa espacio. -->
            <progress-circle v-if="isInProgress(props.row)" :progress="props.row.progress" :status="props.row.status" />
            <!-- Fallo: casteamos a "Error" y el detalle va en un icono con tooltip al pasar. -->
            <template v-if="errorDetail(props.row)">
              <span class="tag status-pill ml-2 is-danger">{{ $t('error') }}</span>
              <b-tooltip :label="errorDetail(props.row)" position="is-top" type="is-dark" multilined>
                <b-icon pack="fas" icon="exclamation-circle" size="is-small"
                        class="ml-1 text-red cursor-pointer" />
              </b-tooltip>
            </template>
            <span v-else class="tag status-pill ml-2" :class="statusVariant(props.row)">{{ statusLabel(props.row.description_status) }}</span>
          </div>
        </b-table-column>

        <b-table-column field="date" :label="$t('generated')" v-slot="props">
          {{ props.row.created_at }}
        </b-table-column>

        <b-table-column field="avatar" :label="$t('avatar')" width="40" v-slot="props">
          <figure>
            <p class="image is-50x50">
              <img class="rounded-full" :src="props.row.user.avatar">
            </p>
          </figure>
        </b-table-column>

        <b-table-column field="user.fullname" :label="$t('user')" sortable v-slot="props">
          {{ props.row.user.fullname }}
        </b-table-column>

        <b-table-column field="type" :label="$t('report_type')" sortable v-slot="props">
          {{ $t(props.row.type) }}
        </b-table-column>

        <b-table-column :label="$t('actions')" field="actions" centered width="100" v-slot="props">
          <div class="w-full h-full flex items-center justify-around">
            <b-tooltip v-if="props.row.progress == 100"
              :label="$t('download')" position="is-left">
              <div @click="downloadButtonClicked(props.row.id)"
                   class="cursor-pointer flex text-blue text-lg">
                <b-icon pack="fas" icon="file-download"/>
              </div>
            </b-tooltip>
            <b-tooltip v-if="props.row.progress == 100"
              :label="$t('regenerate')" position="is-left">
              <div @click="loadButtonClicked(props.row.id)"
                   class="cursor-pointer text-lg">
                <b-icon pack="fas" icon="undo"/>
              </div>
            </b-tooltip>
            <!-- solo quien lanzó el informe puede borrarlo (can_delete viene de ProcesslogPolicy) -->
            <b-tooltip v-if="props.row.can_delete" :label="$t('remove')" position="is-left" type="is-danger">
              <div @click="deleteButtonClicked(props.row.id, props.row.description)"
                   class="cursor-pointer flex text-red text-lg">
                <b-icon pack="fas" icon="trash-alt"/>
              </div>
            </b-tooltip>
          </div>
        </b-table-column>

        <template #empty>
          <div class="has-text-centered">{{ $t('no_results') }}</div>
        </template>
      </b-table>
    </section>
  </div>
</template>

<script>
  import EventBus from '~/plugins/bus'
  import ProgressCircle from '../components/progress-circle.vue'
  import {
    fetchHistory,
    fetchReportPayload,
    deleteLog,
    requestDownload,
    applyRealtimeUpdate,
    statusVariant as statusVariantFn,
    isInProgress as isInProgressFn,
    errorDetail as errorDetailFn,
    reportWeek as reportWeekFn,
    reportTitle as reportTitleFn,
    reportPeriod as reportPeriodFn,
    filterHistory,
    statusOptions,
  } from '../services/history-reports.service'

  export default {

    components: {
      ProgressCircle,
    },

    props: {
      projectId: { type: Number, required: true },
      emitParentList: { type: Boolean, default: false }
    },

    data: function() {
      return {
        isLoadingTable: true,
        history: [],
        perPage: 20,
        searchText: '',
        statusFilter: '',
        weekFilter: '',
      }
    },

    computed: {
      // Filtro client-side (texto + estado). El orden y la paginación los hace el b-table.
      filteredHistory() {
        return filterHistory(this.history, { text: this.searchText, status: this.statusFilter, week: this.weekFilter })
      },
      // Estados distintos presentes, para el desplegable de filtro.
      statusChoices() {
        return statusOptions(this.history)
      },
      // ¿Hay algún filtro activo? (para mostrar el botón "Limpiar filtros").
      hasFilters() {
        return !!(this.searchText || this.weekFilter || this.statusFilter)
      },
    },

    created() {
      this.getHistory()

      EventBus.$on('processQueueLog', (e) => {
        this.history = applyRealtimeUpdate(this.history, e)
      })
    },

    beforeDestroy() {
      EventBus.$off('processQueueLog')
    },

    methods: {

      // description_status puede venir como clave i18n (report_generating, processing,
      // all_completed, pending_to_start...) o como texto plano; traducimos si existe la clave.
      statusLabel(value) {
        if (!value) return ''
        return this.$te(value) ? this.$t(value) : value
      },

      // Color del pill de estado (la etiqueta es description_status, ya traducida).
      statusVariant(row) {
        return statusVariantFn(row)
      },

      // ¿Sigue generándose? (para mostrar el indicador de progreso solo entonces).
      isInProgress(row) {
        return isInProgressFn(row)
      },

      // Mensaje de error de un informe fallido (null si no lo es) para el tooltip.
      errorDetail(row) {
        return errorDetailFn(row)
      },

      // Nº de semana inferido del description (o null) para la columna Semana.
      reportWeek(row) {
        return reportWeekFn(row)
      },

      // Título del informe (description sin la cola ' - Semana…') para la 1ª columna.
      reportTitle(row) {
        return reportTitleFn(row)
      },

      // Periodo { from, to } inferido del description (o null) para la 2ª línea.
      reportPeriod(row) {
        return reportPeriodFn(row)
      },

      async getHistory() {
        this.isLoadingTable = true
        this.history = []
        try {
          this.history = await fetchHistory(this.projectId)
        } catch (e) {
          this.$notify.error(e.message || 'error_retrieving_data')
        } finally {
          this.isLoadingTable = false
        }
      },

      reload() {
        this.getHistory();
      },

      // Limpia los filtros client-side (no recarga del backend; el filtrado es local).
      clearFilters() {
        this.searchText = ''
        this.weekFilter = ''
        this.statusFilter = ''
      },

      async loadButtonClicked(id) {
        try {
          const payload = await fetchReportPayload(id)
          this.$emit('load-report', payload)
        } catch (e) {
          this.$notify.error(e.message || 'error_retrieving_data')
        }
      },

      // El listado de process-logs no trae `name`: identificamos el registro por su descripción.
      deleteButtonClicked(id, description) {
        this.$dialog.confirm({
          title: this.$t('delete'),
          message: this.$t('delete_confirm_text', [this.$t('the_record'), description]),
          confirmText: this.$t('delete_record'),
          type: 'is-danger',
          hasIcon: true,
          onConfirm: async () => {
            try {
              await deleteLog(id)
              this.$notify.success('success_deleting_generic')
              this.getHistory()
            } catch (response) {
              // el interceptor de axios rechaza con error.response (p. ej. 403 si no es tu proceso)
              this.$notify.error((response && response.response && response.response.data && response.response.data.error) || response.message || 'error_deleting')
            }
          }
        })
      },

      async downloadButtonClicked(id) {
        try {
          const url = await requestDownload(id)
          this.$notify.success('generating');
          window.open(url);
        } catch (e) {
          this.$notify.error(e.message || 'error_retrieving_data')
        }
      },

    },

  };
</script>

<style scoped>
/* Pill de estado compacto (más pequeño que el .tag por defecto de Bulma). */
.status-pill {
  height: auto;
  font-size: 0.65rem;
  line-height: 1.5;
  padding: 0.05rem 0.5rem;
}

/* Inputs/selects de filtro (componentes Buefy) con aspecto propio. El tamaño/layout
   va con clases Tailwind en la plantilla (w-*, gap-2, flex); aquí solo el borde/foco
   en azul EPC, pinchando los internos de Buefy con ::v-deep. */
.hr-filters ::v-deep .input,
.hr-filters ::v-deep .select select {
  border-color: #c5d4eb;
  border-radius: 0.375rem;
}
.hr-filters ::v-deep .input:focus,
.hr-filters ::v-deep .select select:focus {
  border-color: #3b66bc;
  box-shadow: 0 0 0 2px rgba(59, 102, 188, 0.15);
}
</style>
